<template>
  <div>
    <LoaderComponent v-if="!bulkStore.properties" :custom-style="'z-index: 100'" />

    <div v-show="bulkStore.properties" class="position-relative">
      <div class="mb-2">
        <!-- Header rows -->
        <div ref="menuHeader" class="menu-header" :style="[productsViewStore.stickyWrapperStyle, store.topMenuStyle]">
          <!-- Action bar buttons -->
          <div
            v-if="store.userPrivileges.has('manageProducts')"
            class="edit-row action-bar"
            :class="[bulkStore.editMode ? 'editable' : 'not-editable']"
            :style="{ width: `calc(100% + ${getScrollbarWidth()})` }"
          >
            <template v-if="bulkStore.editMode">
              <button class="btn btn-outline-primary mr-1" :disabled="bulkStore.isPristine" @click="openPreview()">
                {{ appTranslate('Preview and publish') }}
              </button>

              <button class="btn btn-warning mr-1" @click="handleSoftCancelEditMode()">
                {{ appTranslate('Abort') }}
              </button>
              <input
                v-if="sessionStore.current"
                v-model="sessionStore.current.name"
                :placeholder="appTranslate('Name the session (optional)')"
                class="form-control mr-1"
                style="width: 250px"
                :title="appTranslate('Session name')"
                @keydown.stop
                @change="sessionStore.saveCurrentSession()"
              />
              <button
                v-if="store.userPrivileges.has('manageSyncToWeb')"
                :disabled="!bulkStore.editMode"
                class="btn btn-outline-secondary"
                @click="productsStore.showPriceAdjustmentHandler = true"
              >
                <i class="fa fa-money" />
                <span class="ml-1" :class="{ 'popup shadowed': productsViewStore.shouldBeCollapsed }">
                  {{ appTranslate('Set sale prices') }}
                </span>
              </button>
            </template>

            <template v-else>
              <button class="btn btn-secondary mr-2" @click="handleEnableEditMode()">
                {{ appTranslate('Edit selection') }}
              </button>

              <button
                :disabled="!sessionStore.previousSessions?.length && !sessionStore.discardedSessions?.length"
                class="btn btn-outline-secondary mr-2"
                :title="sessionStore.previousSessions?.length ? undefined : appTranslate('There are no previous sessions')"
                @click="showEditingSessionsHandler = true"
              >
                {{ appTranslate('Continue previous') }}
                <template v-if="sessionStore.previousSessions?.length"> ({{ appTranslate('%s existing', [sessionStore.previousSessions.length]) }}) </template>
              </button>

              <button
                class="btn btn-outline-secondary mr-2"
                :title="appTranslate('Create new products from scratch or use current selection as a starting point for new products')"
                @click="showNewSessionHandler = true"
              >
                {{ appTranslate('Create new products') }}
              </button>
            </template>

            <div class="ml-auto" />

            <button class="link" :title="appTranslate('Toggle fullscreen')" @click="toggleFullscreen()">
              <i class="fa" :class="productsViewStore.fullscreen ? 'fa-compress' : 'fa-expand'" />
            </button>

            <div v-if="bulkStore.editMode" class="mx-4">
              <button
                class="btn btn-light mr-1 position-relative"
                :title="`${appTranslate('Undo')} (${metaKey}+Z)`"
                :disabled="!bulkStore.undoStates"
                @click="bulkStore.undo()"
              >
                <i class="fa fa-undo" />
                <div class="history-counter">{{ bulkStore.undoStates }}</div>
              </button>

              <button
                class="btn btn-light position-relative"
                :title="`${appTranslate('Redo')} (${metaKey}+Y)`"
                :disabled="!bulkStore.redoStates"
                @click="bulkStore.redo()"
              >
                <i class="fa fa-repeat" />
                <div class="history-counter">{{ bulkStore.redoStates }}</div>
              </button>
            </div>
          </div>
          <!--  -->

          <div class="edit-row">
            <PropertySelectMenu
              v-if="bulkStore.properties"
              v-model="bulkStore.selectedPropertyKeys"
              v-model:selected-deadlines="bulkStore.selectedDeadlines"
              :properties="bulkStore.properties"
              :edit-mode="bulkStore.editMode"
              :selected-cultures="bulkStore.selectedCultures"
              view="productTable"
              class="mr-4"
              :forced-properties="bulkStore.stickyProperties"
              :property-keys-included-in-selected-deadlines="bulkStore.propertyKeysIncludedInSelectedDeadlines"
            />

            <PopOverComponent
              id="private-profiles-msg"
              :show-until="new Date('2026-07-15')"
              horizontal-position="start"
              vertical-position="below"
              :style="{ top: '-70px', left: '-140px' }"
            >
              Nu kan du skapa privata profiler som bara du ser. Öppna fältväljaren och scrolla längst ner för att spara en privat profil
            </PopOverComponent>

            <!-- Select cultures -->
            <div class="dropdown mr-4">
              <a
                class="dropdown-toggle link"
                data-toggle="dropdown"
                :title="appTranslate('Choose which cultures (countries and languages) to display your selected cultural fields for')"
              >
                {{ appTranslate('Show cultural fields for') }}
                <img v-for="(culture, i) of bulkStore.selectedCultures" :key="i" :src="getFlag(culture.country)" class="tiny-flag" style="margin-left: 1px" />
              </a>

              <div class="dropdown-menu down" @click.stop>
                <button :disabled="selectedCulturesFollowIncludedCountries" class="dropdown-item link" @click.stop="bulkStore.toggleAllCultures()">
                  {{ bulkStore.allCulturesSelected ? appTranslate('Select none') : appTranslate('Select all') }}
                </button>
                <div class="dropdown-divider" />

                <label
                  class="dropdown-item"
                  :title="appTranslate('Displays selected cultural fields using the same cultures you selected in the Data for menu')"
                  @click.stop
                >
                  <input v-model="selectedCulturesFollowIncludedCountries" type="checkbox" />
                  {{ appTranslate('Same as data for') }}
                </label>

                <label v-for="(culture, i) of store.availableCultures" :key="i" class="dropdown-item" @click.stop>
                  <input
                    :disabled="selectedCulturesFollowIncludedCountries"
                    type="checkbox"
                    :checked="!!cultureIsIncluded(bulkStore.selectedCultures, culture)"
                    @click="bulkStore.toggleCulture(culture)"
                  />
                  <img class="flag small-flag ml-1" :src="getFlag(culture.country)" />
                  {{ getLocaleString(culture) }}
                </label>
              </div>
            </div>

            <div id="table-search-placeholder" />

            <div class="ml-auto" />

            <template v-if="!bulkStore.editMode">
              <!-- Read mode, right side -->
              <label class="ml-2 mb-0" :class="{ disabled: bulkStore.editMode }" :title="appTranslate('Converts values into clickable filtering links')">
                <input v-model="bulkStore.activateProductLinks" type="checkbox" :disabled="bulkStore.editMode" />
                {{ appTranslate('Activate filter links') }}
              </label>
            </template>
          </div>
        </div>

        <!----------->
        <!-- TABLE -->
        <!----------->
        <ProductsTable ref="tableRef" :sticky-top-margin="stickyTopMargin" />

        <div v-if="bulkStore.editMode && productsStore.allProductsLoaded">
          <!-- class="sticky-wrapper px-2 mt-2"
          :style="`${productsViewStore.stickyWrapperStyle}; z-index: 2`" -->
          <Teleport v-if="bulkStore.editMode && productsStore.allProductsLoaded" to="#add-new-rows-placeholder td" defer>
            <fieldset class="add-new-row">
              <legend @click="addNewRow = !addNewRow">
                {{ appTranslate('Add new row(s)') }}
              </legend>

              <div
                ref="addNewRowOptions"
                class="add-new-row-options"
                :class="{ open: addNewRow }"
                :style="`max-height: ${addNewRow && addNewRowOptions ? addNewRowOptions.scrollHeight + 50 : 0}px`"
              >
                <div>
                  <label class="user-select-none">
                    <input v-model="baseNewRowOnExisting" type="checkbox" class="mr-1" />
                    <small>{{ appTranslate('Base on existing product') }}</small>
                  </label>
                </div>

                <small class="d-block">Antal</small>
                <div class="d-flex flex-wrap align-items-start buttons">
                  <input
                    v-model="addNewRowAmount"
                    type="number"
                    class="form-control no-counter mr-1"
                    :style="{ width: '3em' }"
                    min="1"
                    :title="appTranslate('Number of rows to add')"
                    @keydown.stop
                  />

                  <div v-if="baseNewRowOnExisting" class="mr-1" style="width: 350px; min-width: 250px">
                    <ProductMiniCard v-if="baseNewRowOnProduct" :product="baseNewRowOnProduct" style="font-size: 0.8em; margin-top: -13px">
                      <button class="btn ml-2" style="font-size: 2em" @click="baseNewRowOnProduct = undefined" @keydown.stop>&times;</button>
                    </ProductMiniCard>

                    <ProductSearchComponent
                      class="mr-1 d-block"
                      style="width: 350px; min-width: 250px"
                      content-style="font-size: 1rem;"
                      load-more-inline
                      include-culture-exclusive-data
                      :data-culture="store.dataCulture"
                      :included-extra-countries="store.includedCountries"
                      :placeholder="appTranslate('Search product')"
                      @select="baseNewRowOnProduct = $event.value"
                    />
                  </div>

                  <button
                    style="white-space: nowrap"
                    :disabled="baseNewRowOnExisting && !baseNewRowOnProduct"
                    class="btn btn-success w-fit-content mr-2"
                    @click="addNewProduct(addNewRowAmount, baseNewRowOnExisting ? baseNewRowOnProduct : undefined)"
                    @keydown.stop
                  >
                    {{ appTranslate('Add') }}
                  </button>

                  <button class="btn btn-warning" @click="addNewRow = false" @keydown.stop>{{ appTranslate('Close') }}</button>
                </div>
              </div>
            </fieldset>
          </Teleport>
        </div>
      </div>

      <div
        v-if="
          productsStore.allProductsLoaded &&
          bulkStore.recentImportedProductsNotAmongResult?.length &&
          !bulkStore.editMode &&
          bulkStore.showRecentImportedProductsMsg
        "
        class="pl-1 sticky-wrapper"
        :style="productsViewStore.stickyWrapperStyle"
      >
        {{
          appTranslate('%s products were recently edited by you but no longer match your current filter', [
            bulkStore.recentImportedProductsNotAmongResult.length,
          ])
        }}.

        <router-link
          :to="{ name: 'products', query: { _field_sku: bulkStore.recentImportedProductsNotAmongResult.join(','), includeAllCultures: 1 } }"
          class="mr-3"
          @click="bulkStore.latestImportedProducts = undefined"
        >
          {{ appTranslate('Show edited products') }}
        </router-link>
        <button class="link ml-2" style="font-size: 1.2em; line-height: 1rem" @click="bulkStore.latestImportedProducts = undefined">&times;</button>
      </div>

      <!-- <br /> -->

      <!-- <button
        v-if="store.isDevEnvironment"
        class="btn btn-warning dev"
        @click="productsStore.latestImportedProducts = { skipped: [], all: ['GpmqbyeMyp', '666'], imported: ['GpmqbyeMyp', '666'] }"
      >

        Sätt importresultat
      </button> -->
    </div>

    <!------------------------------>
    <!-- Editing session handling -->
    <!------------------------------>
    <ModalComponent
      v-if="showEditingSessionsHandler"
      body-style="padding-top: 0; padding-bottom: 0; min-height: 100px"
      modal-style=""
      @close="closeEditingSessionHandler()"
      @disable="shownInlinePreviews = {}"
    >
      <template #header>
        <div class="d-flex flex-wrap">
          <h3 class="w-100">
            {{ appTranslate('Previous editing sessions') }}
            <template v-if="currentPreviousSessionsMode === 'discardedSessions'"> ({{ appTranslate('trash', undefined, 'lowercase') }}) </template>
          </h3>
        </div>

        <select v-model="currentPreviousSessionsMode" class="ml-auto">
          <option v-for="(mode, i) of previousSessionsModes" :key="i" :value="mode.value">{{ mode.displayValue }}</option>
        </select>
      </template>

      <div class="sessions">
        <div v-for="(editingSession, i) of sessionStore[currentPreviousSessionsMode]" :key="i" class="editing-session session">
          <h4 v-if="editingSession.name" :title="appTranslate('Name')">"{{ editingSession.name }}"</h4>
          <h4 v-else class="disabled">Session #{{ i + 1 }}</h4>

          <div class="my-2">
            <small v-if="editingSession.checkedOut && store.isDevEnvironment" class="btn-warning p-2" style="border-radius: 5px">Checked out!</small>
          </div>

          <template v-if="editingSession.date">
            <strong class="mr-1">{{ appTranslate('Latest change') }}:</strong>
            {{ new Date(String(editingSession.date))?.toLocaleString() }}
          </template>

          <ActiveFiltersComponent
            class="mb-3"
            :query="editingSession.query || {}"
            :data-culture="editingSession.dataCulture"
            :included-countries="editingSession.includedCountries"
            :include-products-missing-available-countries="editingSession.includeProductsMissingAvailableCountries"
            show-included-countries
          />

          <div v-html="appTranslate('Contains changes for %s products', [`<strong>${Object.keys(editingSession.edited || {}).length}</strong>`])" />

          <div class="preview-wrapper" :class="[shownInlinePreviews[editingSession.id] ? 'expanded-preview' : 'collapsed-preview']">
            <BulkEditPreviewComponent
              v-if="shownInlinePreviews[editingSession.id]"
              :key="currentPreviousSessionsMode"
              :product-input-by-sku="Object.fromEntries(getProductInputEntries(editingSession.edited))"
              :preview-properties="bulkStore.previewProperties"
              class="preview"
            />
          </div>

          <div class="d-flex mt-2">
            <button
              v-if="currentPreviousSessionsMode === 'discardedSessions'"
              class="btn btn-success mr-2"
              @click="sessionStore.restoreDiscardedSession(editingSession.id!)"
            >
              {{ appTranslate('Restore session') }}
            </button>
            <button v-else class="btn btn-primary mr-2" @click="loadEditingSession(editingSession.id!)">
              {{ appTranslate('Continue editing') }}
            </button>

            <button
              class="btn btn-light"
              :title="appTranslate(shownInlinePreviews[editingSession.id] ? 'Close preview' : 'Show preview')"
              @click="shownInlinePreviews[editingSession.id] = !shownInlinePreviews[editingSession.id]"
            >
              <i class="fa" :class="[shownInlinePreviews[editingSession.id] ? 'fa-search-minus' : 'fa-search-plus']" />
            </button>

            <button class="btn btn-danger ml-auto" @click="sessionStore.removeSession(editingSession.id!, currentPreviousSessionsMode === 'discardedSessions')">
              {{ appTranslate(currentPreviousSessionsMode === 'discardedSessions' ? 'Delete permanently' : 'Discard') }}
            </button>
          </div>
        </div>

        <div v-if="!sessionStore[currentPreviousSessionsMode]?.length" class="my-2">
          {{ appTranslate(currentPreviousSessionsMode === 'discardedSessions' ? 'The bin is empty' : 'You have no previous editing sessions') }}
        </div>
      </div>
    </ModalComponent>

    <!------------------->
    <!-- Preview modal -->
    <!------------------->
    <ModalComponent v-if="showPreview" @close="showPreview = false">
      <h3>
        {{ appTranslate('Preview and publish') }}
      </h3>

      <!-- Progress -->
      <div v-if="store.activeProgresses.productsBulkEdit?.length" class="text-center">
        <ProgressComponent v-for="(progress, i) of store.activeProgresses.productsBulkEdit" :key="i" :progress="progress" />
      </div>

      <BulkEditPreviewComponent
        v-else
        :product-input-by-sku="bulkStore.productInputBySku"
        :preview-properties="bulkStore.previewProperties"
        :new-products-by-sku="bulkStore.newProductsBySku"
        always-resettable
      />

      <!-- Buttons -->
      <template #footer>
        <ActionPendingButton :disabled="bulkStore.isPristine" :pending="committing" class="btn btn-primary mr-2" @click="commit()">
          {{ appTranslate('Publish') }}
        </ActionPendingButton>
        <ActionPendingButton class="btn btn-warning" @click="showPreview = false">
          {{ appTranslate('Close') }}
        </ActionPendingButton>
      </template>
    </ModalComponent>

    <!----------------------------------->
    <!-- Handle session on abort modal -->
    <!----------------------------------->
    <ModalComponent v-if="showHandleCurrentSessionModal" modal-style="width: fit-content" :dismissible="false" @close="showHandleCurrentSessionModal = false">
      <p>
        {{ appTranslate("You're leaving edit mode. Your changes have not yet been committed.") }}
      </p>
      <button class="btn btn-primary mr-2" @click="showHandleCurrentSessionModal = false">{{ appTranslate('Continue now') }}</button>
      <button class="btn btn-secondary mr-2" @click="handleStopEditing">{{ appTranslate('Continue later') }}</button>
      <button class="btn btn-danger" @click="handleDiscardCurrentSession">
        {{ appTranslate('Discard session') }}
      </button>
    </ModalComponent>

    <!----------------------->
    <!-- New session modal -->
    <!----------------------->
    <ModalComponent v-if="showNewSessionHandler" modal-style="width: fit-content" @close="showNewSessionHandler = false">
      <template #header>
        <h6>
          {{ appTranslate('Do you want to create a blank session or use your current selection as a starting point?') }}
        </h6>
      </template>

      <div class="d-flex">
        <button
          class="btn btn-success mr-2"
          :disabled="!productsStore.currentProducts?.length"
          @click="(createSessionFromCurrentSelection(), (showNewSessionHandler = false))"
        >
          {{ appTranslate('Create session from current selection') }}
        </button>

        <button class="btn btn-primary mr-2" @click="(createBlankSession(), (showNewSessionHandler = false))">
          {{ appTranslate('Blank session') }}
        </button>
      </div>
    </ModalComponent>

    <!-------------------->
    <!-- Set Sale Price -->
    <!-------------------->
    <PriceAdjustmentComponent
      v-show="productsStore.showPriceAdjustmentHandler"
      :activated="productsStore.showPriceAdjustmentHandler"
      source-property-path="recommendedPriceInclVAT"
      target-property-path="salePrice"
      @close="productsStore.showPriceAdjustmentHandler = false"
    >
      <template #header>
        <h5>Prissänkning (rea)</h5>
      </template>
    </PriceAdjustmentComponent>
  </div>
</template>

<script lang="ts" setup>
import ActionPendingButton from '@/components/ActionPendingButton.vue'
import ModalComponent from '@/components/ModalComponent.vue'
import ProductsTable from '@/components/products/archive/table/ProductsTable.vue'
import ActiveFiltersComponent from '@/components/products/filter/ActiveFiltersComponent.vue'
import PropertySelectMenu from '@/components/products/PropertySelectMenu.vue'
import ProgressComponent from '@/components/ProgressComponent.vue'
import LoaderComponent from '@/components/simple/LoaderComponent.vue'
import ProductMiniCard from '@/components/simple/ProductMiniCard.vue'
import { useBarcodes } from '@/composables/useBarcodes'
import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { BooleanObject, Modify } from '@/models/GenericInterfaces'
import { Option } from '@/models/Global'
import { ProductInputRecord } from '@/models/Import'
import Message from '@/models/Message'
import { SimplifiedProduct } from '@/models/Product'
import DataService from '@/services/DataService'
import ImportService from '@/services/ImportService'
import PropertyService from '@/services/PropertyService'
import { useDefaultStore } from '@/store'
import { BlankQuery, BlankQueryKey, UsedAsBaseKey, useEditingSessionsStore } from '@/store/editingSessionsStore'
import { useProductsStore } from '@/store/productsArchiveStore'
import { useProductsBulkStore } from '@/store/productsBulkStore'
import { useProductsViewStore } from '@/store/productsViewStore'
import { usePropertyStore } from '@/store/propertyStore'
import { useTaxonomiesStore } from '@/store/taxonomiesStore'
import { browserStorage } from '@/utils/browserStorage'
import { getDebouncer } from '@/utils/Debouncer'
import { getProductInputEntries } from '@/utils/importUtils'
import { clone, cultureIsIncluded, getLocaleString, getScrollbarWidth, metaKey, objectsAreEqual, toComparable } from '@/utils/tools'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { LocationQuery, useRoute, useRouter } from 'vue-router'
import PriceAdjustmentComponent from '../../PriceAdjustmentComponent.vue'
import ProductSearchComponent from '../../ProductSearchComponent.vue'
import BulkEditPreviewComponent from '../BulkEditPreviewComponent.vue'
import PopOverComponent from '@/components/simple/PopOverComponent.vue'

// -----------
// -- Setup --
// -----------

const menuResizeObserver = new ResizeObserver(
  getDebouncer(() => {
    menuHeaderHeight.value = menuHeader.value?.clientHeight ?? 0
  }, 50),
)

// --------------------------
// -- Composables & Stores --
// --------------------------
const store = useDefaultStore()
const productsStore = useProductsStore()
const bulkStore = useProductsBulkStore()
const productsViewStore = useProductsViewStore()
const propertyStore = usePropertyStore()

const { getFlag } = useTools()
const { translate, appTranslate } = useTranslation()
const router = useRouter()
const route = useRoute()
const sessionStore = useEditingSessionsStore()
const { checkBarcodeCollisions } = useBarcodes()

type PreviousSessionMode = 'previousSessions' | 'discardedSessions'

// -------------------
// -- Reactive vars --
// -------------------
const committing = ref(false)

const showPreview = ref(false)
const shownInlinePreviews = ref<BooleanObject>({})

const tableRef = ref<InstanceType<typeof ProductsTable>>()
const showEditingSessionsHandler = ref(false)
const showHandleCurrentSessionModal = ref(false)
const showNewSessionHandler = ref(false)

const addNewRowAmount = ref(1)
const addNewRow = ref(false)
const addNewRowOptions = ref<HTMLElement>()
const baseNewRowOnExisting = ref(false)
const baseNewRowOnProduct = ref<SimplifiedProduct>()
const menuHeader = ref<HTMLElement>()

const menuHeaderHeight = ref(0)

const currentPreviousSessionsMode = ref<PreviousSessionMode>('previousSessions')

const selectedCulturesFollowIncludedCountries = ref(browserStorage.get('productTable-selectedCulturesFollowIncludedCountries'))

/** used in sessionProducts watcher to trigger updates. necessary if the router navigates on loading session or creating new session */
const watcherShouldRestore = ref(false)

// --------------
// -- Computed --
// --------------

const stickyTopMargin = computed(() => menuHeaderHeight.value + store.topMenuHeight)

const previousSessionsModes = computed((): Array<Modify<Option, { value: PreviousSessionMode }>> => [
  {
    value: 'previousSessions',
    displayValue: appTranslate('Active (%s)', [sessionStore.previousSessions?.length || 0]),
    disabled: !sessionStore.previousSessions?.length,
  },
  {
    value: 'discardedSessions',
    displayValue: appTranslate('Bin (%s)', [sessionStore.discardedSessions?.length || 0]),
    disabled: !sessionStore.discardedSessions?.length,
  },
])

// --------------
// -- Watchers --
// --------------
watch(
  () => bulkStore.sessionProducts,
  async (newValue, prevValue) => {
    if (bulkStore.editMode) {
      if (watcherShouldRestore.value) {
        // trim state to not include empty inputs from the old query when navigated
        bulkStore.trimCurrentState()
        await bulkStore.restoreMissingProducts()
        watcherShouldRestore.value = false
      }

      bulkStore.ensureFullProductInputs() // needed on scroll and when user is navigated to new route on loading editing session or creating new blank session or session with starting points
      bulkStore.updateAllModified(Object.keys(bulkStore.currentState))
    }
  },
)

watch(
  () => store.translationLanguage,
  (language) => {
    bulkStore.properties?.forEach((property) => {
      property.header = PropertyService.getTranslatedHeader(property, language)
    })
  },
)

watch(menuHeader, () => {
  if (menuHeader.value) {
    menuResizeObserver.observe(menuHeader.value)
  }
})

watch(showEditingSessionsHandler, () => {
  sessionStore.fetchSessions()
})

watch(currentPreviousSessionsMode, (value) => {
  sessionStore.fetchSessions(value === 'discardedSessions')
})

watch([() => store.includedCountries, () => selectedCulturesFollowIncludedCountries.value], () => {
  browserStorage.set('productTable-selectedCulturesFollowIncludedCountries', !!selectedCulturesFollowIncludedCountries.value)
  if (selectedCulturesFollowIncludedCountries.value) {
    bulkStore.selectedCultures = store.availableCultures.filter((culture) => store.includedCountries.has(culture.country))
  }
})

// ----------------------
// -- Life cycle hooks --
// ----------------------
onMounted(() => {
  window.addEventListener('beforeunload', sessionStore.handleCurrentSessionOnBreak)
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', sessionStore.handleCurrentSessionOnBreak)
})

// ---------------
// -- Functions --
// ---------------
// const closeFullscreenListener = (e: KeyboardEvent) => {
//   if (e.key === 'Escape' && !productsStore.editMode) {
//     store.fullscreen = false
//   }
// }

// despite its name does not belong in the session store. affects internal variables and session is sent as parameter
const loadEditingSession = async (sessionId: string | number) => {
  try {
    const session = await DataService.checkOutSession(sessionId)

    if (
      session &&
      (!session?.checkedOut ||
        confirm(appTranslate('The session is already checked out. If you check it out again you risk losing data if editing in different browser tabs')))
    ) {
      const previousQuery = {
        includedCountries: store.includedCountries,
        explicityIncludeProductsMissingAvailableCountries: store.explicityIncludeProductsMissingAvailableCountries,
        // fullPath: router.currentRoute.value.fullPath,
      }

      store.dataCulture = session.dataCulture
      store.explicitlySelectedCountries = new Set(session.includedCountries)
      store.explicityIncludeProductsMissingAvailableCountries = !!session.includeProductsMissingAvailableCountries

      sessionStore.current = {
        id: session.id,
        name: session.name,
      }

      const nonFilterQueries = Object.entries(route.query).filter(([key]) => !key.startsWith('_'))

      const navigationFailure = await router.push({ query: { ...Object.fromEntries(nonFilterQueries), ...session.query } })
      // await router.push({ query: { ...Object.fromEntries(nonFilterQueries), ...session.query, includedCountries: session.includedCountries.join(',') } })

      watcherShouldRestore.value =
        !navigationFailure &&
        // previousQuery.fullPath === router.currentRoute.value.fullPath
        previousQuery.explicityIncludeProductsMissingAvailableCountries === store.explicityIncludeProductsMissingAvailableCountries &&
        objectsAreEqual(previousQuery.includedCountries, store.includedCountries)

      // fix legacy sessions that always contained sku
      for (const id in session.edited) {
        const productInput = session.edited[id]
        if (id === productInput?.sku) {
          delete productInput?.sku
        }
      }
      session.edited = Object.fromEntries(getProductInputEntries(session.edited))

      showPreview.value = false
      showEditingSessionsHandler.value = false

      if (!watcherShouldRestore.value) {
        await bulkStore.restoreMissingProducts()
      }

      setEditMode(session.edited).then(() => {
        if (session.stagedOptions) {
          propertyStore.setStagedPropertyOptions(session.stagedOptions)
        }
      })
      productsStore.loading.delete('restore')
    }
  } catch (err) {
    store.addMessage(new Message(appTranslate('An error occurred'), appTranslate('Could not check out session'), 'danger'))
    console.log(err)
  }
}

const createBlankSession = async () => {
  await router.push({ query: BlankQuery })
  await handleEnableEditMode()
  watcherShouldRestore.value = true
  addNewProduct(1)
}

const createSessionFromCurrentSelection = async () => {
  if (
    productsStore.currentTotalProducts < 500 ||
    confirm(appTranslate(`Are you sure you want to load data from ${productsStore.currentTotalProducts} products?`))
  ) {
    productsStore.loading.add('startingPoints')
    const startingPointPromise = ImportService.getProductStartingPoints(route.query)
    await router.push({ query: { [UsedAsBaseKey]: 'yes', ...route.query } })

    const startingPoints = await startingPointPromise
    const temporarySkus = await ImportService.getTemporarySkus(startingPoints.length)

    const initialState: ProductInputRecord = {}
    const newProducts: SimplifiedProduct[] = []

    startingPoints.forEach((productInput, i) => {
      const tempSku = temporarySkus[i]
      bulkStore.currentState[tempSku] = productInput
      bulkStore.updateModified(tempSku)

      initialState[tempSku] = productInput

      newProducts.push({
        sku: tempSku,
        type: productInput.type ?? 'product',
        hasTemporarySku: true,
        categories: [],
        tags: [],
        collections: [],
        attributes: [],
      })
    })

    bulkStore.newProducts = newProducts

    await handleEnableEditMode(initialState)

    productsStore.loading.delete('startingPoints')
  }
}

/**
 * Sets edit mode, preparing certain variables. Also saves current session if set to false
 * @param state an empty object initiates a new session, null exits editMode. use a saved state to continue a previous session
 * @param replaceRouteQuery whether router should remove temporary edit filters like usedAsBase and blankSession or navigate to a new route (e.g. navigate to the created products if edit happened from a blank query)
 */
const setEditMode = async (state: ProductInputRecord | null, replaceRouteQuery?: boolean | LocationQuery) => {
  bulkStore.editMode = !!state
  propertyStore.resetStagedPropertyOptions()

  if (state) {
    void DataService.loadPropertyInstructions(true)
    void DataService.loadAvailableBarcodeOptions(true)

    store.evaluateNavigation = {
      path: route.path,
      cb: async () => {
        if (
          bulkStore.isPristine ||
          confirm(
            translate(
              'Do you want to leave the editing session? No changes have been committed yet, but the session can be resumed later.',
              'application',
              'capitalize',
            ),
          )
        ) {
          await sessionStore.handleCurrentSessionOnBreak()
          setEditMode(null)
          return true
        } else {
          return false
        }
      },
    }
    store.clearProgress('productsBulkEdit')
    bulkStore.initiateState(state)

    if (!watcherShouldRestore.value) {
      // no navigation. will not be caught by sessionProducts watcher
      bulkStore.ensureFullProductInputs()
      bulkStore.updateAllModified(Object.keys(state))
    }
  } else {
    bulkStore.clearState()
    store.evaluateNavigation = undefined
    sessionStore.current = undefined
    bulkStore.modified = {}
    bulkStore.extraProducts = []
    bulkStore.newProducts = []
    showHandleCurrentSessionModal.value = false

    if (replaceRouteQuery) {
      if (replaceRouteQuery === true) {
        const query = { ...route.query }
        delete query[UsedAsBaseKey]
        delete query[BlankQueryKey]
        await router.replace({ query })
      } else {
        await router.replace({ query: replaceRouteQuery })
      }
    }
  }
}

/**
 * Checks for existing sessions to potentially continue on
 * Ensures a session id is set and then enables editmode
 */
const handleEnableEditMode = async (initialState: ProductInputRecord = {}) => {
  await sessionStore.fetchSessions()
  const existingSessions = sessionStore.getExistingSessions(route.query)

  // if (existingSession && confirm(appTranslate('A previous session using the same selection was found. Do you want to continue on that session?'))) {
  //   sessionStore.current = { id: existingSession.id, name: existingSession.name }
  //   await loadEditingSession(existingSession.id!)
  //   return
  // } else {
  sessionStore.current = { id: await DataService.getNewSessionId() }
  // }

  if (existingSessions.length) {
    store.addMessage(
      new Message(
        appTranslate('Starting new session'),
        appTranslate('Note that %s previous session(s) using the same filters was found', [existingSessions.length]),
        'info',
        10,
      ),
    )
  }

  await setEditMode(initialState)
}

const handleSoftCancelEditMode = async () => {
  if (bulkStore.isPristine) {
    await sessionStore.handleCurrentSessionOnBreak()
    await cancelEditModeAndMaybeNavigate()
  } else {
    // show decision modal
    showHandleCurrentSessionModal.value = true
  }
}

const handleDiscardCurrentSession = async () => {
  const sessionId = sessionStore.current?.id
  await handleStopEditing() // save before discarding, making sure binned item represents the latest changes

  if (sessionId) {
    sessionStore.removeSession(sessionId, undefined, undefined, true)
  }
}

const handleStopEditing = async () => {
  await sessionStore.saveCurrentSession(0, true)
  await cancelEditModeAndMaybeNavigate()
}

const cancelEditModeAndMaybeNavigate = async () => {
  let goBack = false
  if (route.query[BlankQueryKey] || route.query[UsedAsBaseKey]) {
    goBack = true
  }
  await setEditMode(null, true)
  goBack && router.back()
}

const commit = async () => {
  if (confirm(appTranslate('Would you like to publish changes for %s products?', [Object.keys(bulkStore.productInputBySku).length]))) {
    committing.value = true
    try {
      const barcodeCollisionCheck = await checkBarcodeCollisions(bulkStore.productInputEntries.map(([id, productInput]) => ({ sku: id, ...productInput })))
      if (!barcodeCollisionCheck.ok && !confirm(barcodeCollisionCheck.msg)) {
        return
      }

      const result = await ImportService.importProducts(bulkStore.productInputBySku, undefined, { all: true }, false, 'productsBulkEdit')

      // refresh local taxonomy caches
      // const taxonomyRefresh = RegisteredTaxonomies.map((taxonomy) => DataService.getTaxonomy(taxonomy, undefined, true))
      useTaxonomiesStore().clearTaxonomicCache()

      // productsStore.fetchProducts(true)
      productsStore.currentProducts.fill(undefined) // triggers fetch without disrupting current scroll

      // await Promise.all(taxonomyRefresh)
      const id = sessionStore.current?.id

      const replaceRouteQuery =
        (route.query[UsedAsBaseKey] || route.query[BlankQueryKey]) && result.imported?.length
          ? { _field_sku: result.imported.join(','), includeAllCultures: '1' }
          : true
      setEditMode(null, replaceRouteQuery)

      sessionStore.removeSession(id!, false, true, true)
    } catch (err: any) {
      store.addMessage(new Message(appTranslate('An error occurred'), err.response?.data || err.message || err, 'danger'))
      store.clearProgress('productsBulkEdit')
      console.error(err)
    } finally {
      committing.value = false
    }
  }
}

const addNewProduct = async (amount: number, productStartingPoint?: SimplifiedProduct) => {
  try {
    const temporarySkus = await ImportService.getTemporarySkus(amount)

    const startingPoint = await ImportService.getProductStartingPoint(productStartingPoint?.sku)

    for (let i = 0; i < amount; i++) {
      ;(async () => {
        const product = {
          sku: temporarySkus[i],
          hasTemporarySku: true,
        }
        bulkStore.currentState[product.sku] = clone(startingPoint)
        bulkStore.updateModified(product.sku)

        bulkStore.newProducts.push(product as SimplifiedProduct)
      })()
    }

    bulkStore.pushHistoryEntry()
  } catch (err: any) {
    store.addMessage(
      new Message(appTranslate('An error occured'), 'Ett fel uppstod när ny produktrad skulle läggas till. ' + JSON.stringify(err?.response?.data), 'danger'),
    )
    console.log(err)
  }
}

const toggleFullscreen = async (activate?: boolean) => {
  const newState = activate === undefined ? !productsViewStore.fullscreen : activate
  productsViewStore.fullscreen = newState
}

const closeEditingSessionHandler = () => {
  showEditingSessionsHandler.value = false
  currentPreviousSessionsMode.value = 'previousSessions'
  shownInlinePreviews.value = {}
}

const openPreview = () => {
  showPreview.value = true
  // bulkStore.updateAllModified(Object.keys(bulkStore.currentState)) // should reset properties that are no longer modified
}
</script>

<style lang="scss" scoped>
$buttonBorder: 1px solid #d2d2d2;

.dropdown-menu-right {
  right: 0;
  left: unset !important;
}

.menu-header {
  position: sticky; // to enable positioning it above the sticky headers
  z-index: 8;
  border-bottom: $buttonBorder;
  user-select: none;

  .edit-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    padding: 5px 2em;

    background-color: var(--table-secondary-header-colour);
    border-top: 1px solid #ddd;

    &.action-bar {
      border-color: #ccc;
      background-color: var(--table-primary-header-colour);

      &.editable {
        $borderWidth: 2px;
      }
    }

    &:not(:last-child) {
      border-right-color: transparent;
    }

    button:not(.link) {
      border-radius: 3px;
      border: 1px solid #aaa;

      &.btn-outline-primary,
      &.btn-outline-warning,
      &.btn-outline-secondary {
        &:not(:hover),
        &:disabled {
          background-color: white;
        }
      }
    }

    fieldset {
      padding-bottom: 0;
    }
  }
}

// Session handler
.sessions {
  .session {
    padding: 2em;
    margin: 0 -2em;
    border-top: 1px solid #ccc;

    &:nth-child(2n) {
      background: #f5f5f5;
    }
  }

  .preview-wrapper {
    transition: height 0.5s;
    overflow: auto;
    padding: 3px;
    margin-bottom: 1em;

    &.collapsed-preview {
      height: 0;
    }

    &.expanded-preview {
      height: 300px;
    }

    .preview {
      height: 100%;
    }
  }
}

.footer {
  box-shadow: 0 -8px 6px -6px #eee;
}

@keyframes pulse {
  0% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.5;
  }
}

.history-counter {
  position: absolute;
  left: 2px;
  bottom: 0;
  font-size: 10px;
  color: grey;
}

:deep() {
  fieldset.add-new-row {
    padding: 0.25em 0.5em;
    border-radius: 5px;
    border: 1px solid #ddd;
    background-color: #f8f8f8;
    margin-right: 5px;

    button,
    input[type='number'],
    .search {
      margin-bottom: 3px;
    }

    legend {
      font-size: 1.1em;
      display: inline-block;
      width: unset;
      margin: 0 4px;
      padding: 0 2px;

      cursor: pointer;
      user-select: none;
    }

    .add-new-row-options {
      transition: max-height 0.3s;
      overflow: hidden;

      &.open {
        overflow: visible;
      }
    }
  }
}
</style>
